import React, { useEffect, useState } from "react";
import { api, sendMessage, uploadMedia, dataUrlToBase64 } from "../api";
import { useApp } from "../context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { JobPickerDialog } from "./StructuredDialogs";
import { toast } from "sonner";
import { Briefcase, Users as UsersIcon, Send, X, Search } from "lucide-react";

/**
 * After saving a markup (e.g. over a PDF), ask:
 *  1. "Who do you want to send it to?" (contact or group conversation)
 *  2. "Link to a job?" (optional, pre-filled with the source job)
 * Then sends the annotated image as a photo message.
 */
export function SendMarkupDialog({ open, onClose, dataUrl, textOverlays, defaultJobId, onSent }) {
  const { user, t } = useApp();
  const [users, setUsers] = useState([]);
  const [convs, setConvs] = useState([]);
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState(null); // {kind: "user"|"conv", id, name}
  const [job, setJob] = useState(null);
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTarget(null);
    setSearch("");
    api.get("/users").then((res) => setUsers(res.data.filter((u) => u.id !== user.id))).catch(() => {});
    api.get(`/conversations?user_id=${user.id}`).then((res) => setConvs(res.data.filter((c) => c.type !== "dm"))).catch(() => {});
    if (defaultJobId) {
      api.get(`/jobs/${defaultJobId}`).then((res) => setJob(res.data)).catch(() => setJob(null));
    } else {
      setJob(null);
    }
  }, [open, defaultJobId, user.id]);

  const q = search.toLowerCase();
  const filteredConvs = convs.filter((c) => (c.name || "").toLowerCase().includes(q));
  const crewNum = (u) => {
    const m = (u.crew_id || "").replace("crew-", "").toUpperCase().match(/^([A-Z]+)\s*(\d+)$/);
    return m ? parseInt(m[2], 10) : Number.MAX_SAFE_INTEGER;
  };
  const filteredUsers = users
    .filter((u) => u.name.toLowerCase().includes(q))
    .sort((a, b) => crewNum(a) - crewNum(b) || a.name.localeCompare(b.name));

  const doSend = async () => {
    if (!target || !dataUrl) return;
    setBusy(true);
    try {
      let convId = target.id;
      if (target.kind === "user") {
        const res = await api.post("/conversations/dm", { user_id: user.id, other_user_id: target.id });
        convId = res.data.id;
      }
      const media = await uploadMedia({
        base64: dataUrlToBase64(dataUrl),
        jobId: job?.id || null,
        uploadedBy: user.id,
        versionLabel: `Markup — ${user.name.split(" ")[0]}`,
        textOverlays: textOverlays || [],
      });
      await sendMessage({
        conversation_id: convId,
        sender_id: user.id,
        type: "photo",
        content: "",
        media_ids: [media.id],
        meta: job ? { linked_job_id: job.id, link_visibility: "private" } : {},
      });
      toast.success(`${t("send")} ✓`);
      onClose();
      if (onSent) onSent(convId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("something_wrong"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
        <DialogContent className="max-w-md z-[320] max-h-[85vh] flex flex-col" data-testid="send-markup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("send_markup_title")}</DialogTitle>
          </DialogHeader>

          {dataUrl && (
            <img src={dataUrl} alt="markup" className="w-full max-h-32 object-contain rounded-md border border-border bg-muted/40" data-testid="send-markup-preview" />
          )}

          {/* 1. Recipient */}
          <div className="space-y-2 flex-1 min-h-0 flex flex-col">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("send_to_whom")}</div>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="send-markup-search"
                className="pl-8 h-9"
                placeholder={t("search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex-1 overflow-y-auto min-h-[120px] max-h-[240px] space-y-1 pr-1">
              {filteredConvs.length > 0 && (
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1 pt-1">{t("job_groups")}</div>
              )}
              {filteredConvs.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid="send-markup-conv-option"
                  onClick={() => setTarget({ kind: "conv", id: c.id, name: c.name })}
                  className={`w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${target?.id === c.id ? "bg-primary/15 border border-primary/40" : "hover:bg-muted border border-transparent"}`}
                >
                  <Briefcase className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-medium truncate flex-1">{c.name}</span>
                </button>
              ))}
              {filteredUsers.length > 0 && (
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground px-1 pt-2">{t("contacts")}</div>
              )}
              {filteredUsers.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  data-testid="send-markup-user-option"
                  onClick={() => setTarget({ kind: "user", id: u.id, name: u.name })}
                  className={`w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors ${target?.id === u.id ? "bg-primary/15 border border-primary/40" : "hover:bg-muted border border-transparent"}`}
                >
                  <span
                    className="h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                    style={{ backgroundColor: u.avatar_color || "#64748B" }}
                  >
                    {u.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium truncate">{u.name}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">
                      {t(u.role)}{u.crew_id ? ` · ${u.crew_id.replace("crew-", "").toUpperCase()}` : ""}
                    </span>
                  </span>
                </button>
              ))}
              {filteredConvs.length === 0 && filteredUsers.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">{t("no_results")}</p>
              )}
            </div>
          </div>

          {/* 2. Job link (optional) */}
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("link_to_job_q")}</div>
            {job ? (
              <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-2" data-testid="send-markup-job-chip">
                <Briefcase className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-mono-plex font-semibold text-primary">#{job.job_number}</span>
                <span className="text-xs text-muted-foreground truncate flex-1">{job.customer_name}</span>
                <button type="button" data-testid="send-markup-job-clear" onClick={() => setJob(null)} className="text-muted-foreground hover:text-destructive">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <Button data-testid="send-markup-pick-job" variant="outline" size="sm" className="w-full" onClick={() => setJobPickerOpen(true)}>
                <Briefcase className="h-4 w-4 mr-1.5" /> {t("choose_job")}
              </Button>
            )}
          </div>

          <Button data-testid="send-markup-submit" className="w-full" onClick={doSend} disabled={!target || busy}>
            <Send className="h-4 w-4 mr-1.5" />
            {busy ? "..." : target ? `${t("send")} → ${target.name}` : t("send")}
          </Button>
        </DialogContent>
      </Dialog>

      <JobPickerDialog
        open={jobPickerOpen}
        onClose={() => setJobPickerOpen(false)}
        onPick={(j) => { setJob(j); setJobPickerOpen(false); }}
        title={t("choose_job")}
        zClass="z-[340]"
      />
    </>
  );
}
